import { useEffect, useRef, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate, useNavigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";

// Pages
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import AuthCallback from "./pages/AuthCallback";
import CreateProfilePage from "./pages/CreateProfilePage";
import AthleteDashboard from "./pages/AthleteDashboard";
import CoachDashboard from "./pages/CoachDashboard";
import ScoutDashboard from "./pages/ScoutDashboard";
import AdminDashboard from "./pages/AdminDashboard";
import PublicAthletePage from "./pages/PublicAthletePage";
import MessagingPage from "./pages/MessagingPage";
import LeaderboardPage from "./pages/LeaderboardPage";
import BlogPostPage from "./pages/BlogPostPage";
import NewsDeskPage from "./pages/NewsDeskPage";
import TuitionCollegesPage from "./pages/TuitionCollegesPage";
import PlayerHubStubPage from "./pages/PlayerHubStubPage";
import SSOLaunchPage from "./pages/SSOLaunchPage";
import SharedProfilePage from "./pages/SharedProfilePage";
import ApiDocsPage from "./pages/ApiDocsPage";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // If user data was passed from AuthCallback, use it
    if (location.state?.user) {
      setUser(location.state.user);
      setIsAuthenticated(true);
      return;
    }

    const checkAuth = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(`${API_URL}/api/auth/me`, {
          credentials: "include",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });

        if (!response.ok) throw new Error("Not authenticated");

        const userData = await response.json();
        setUser(userData);
        setIsAuthenticated(true);
      } catch (error) {
        setIsAuthenticated(false);
        navigate("/login", { state: { from: location.pathname } });
      }
    };

    checkAuth();
  }, [navigate, location]);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-white text-lg font-display">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  // Check role-based access
  if (allowedRoles.length > 0 && !allowedRoles.includes(user?.role)) {
    // Redirect to appropriate dashboard based on role
    const roleRoutes = {
      athlete: "/dashboard/athlete",
      coach: "/dashboard/coach",
      scout: "/dashboard/scout",
      agent: "/dashboard/agent",
      admin: "/dashboard/admin",
    };
    return <Navigate to={roleRoutes[user?.role] || "/dashboard/athlete"} replace />;
  }

  return children;
};

// App Router with session_id detection
function AppRouter() {
  const location = useLocation();

  // CRITICAL: Check URL fragment for session_id synchronously during render
  // This prevents race conditions by processing session_id FIRST
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/create-profile" element={
        <ProtectedRoute allowedRoles={["athlete"]}>
          <CreateProfilePage />
        </ProtectedRoute>
      } />
      <Route path="/athlete/:athleteId" element={<PublicAthletePage />} />
      <Route path="/leaderboard" element={<LeaderboardPage />} />
      <Route path="/newsdesk" element={<NewsDeskPage />} />
      <Route path="/tuition-colleges" element={<TuitionCollegesPage />} />
      <Route path="/blog/:postId" element={<BlogPostPage />} />
      {/* Token-gated shared private profile (the magic link a coach receives) */}
      <Route path="/share/:token" element={<SharedProfilePage />} />
      {/* Public API / Integration Documentation — shareable with partners */}
      <Route path="/docs/api" element={<ApiDocsPage />} />
      <Route path="/docs/integrations" element={<ApiDocsPage />} />

      {/* Player Hub stub pages (internal sub-routes from the Player Dashboard left sidebar) */}
      <Route path="/coach-portal" element={<ProtectedRoute><PlayerHubStubPage /></ProtectedRoute>} />
      <Route path="/insights" element={<ProtectedRoute><PlayerHubStubPage /></ProtectedRoute>} />
      <Route path="/recruiting-guide" element={<ProtectedRoute><PlayerHubStubPage /></ProtectedRoute>} />
      <Route path="/ncaa-rules" element={<ProtectedRoute><PlayerHubStubPage /></ProtectedRoute>} />
      <Route path="/premium-videos" element={<ProtectedRoute><PlayerHubStubPage /></ProtectedRoute>} />
      <Route path="/weekly-zoom" element={<ProtectedRoute><PlayerHubStubPage /></ProtectedRoute>} />
      <Route path="/live-zoom" element={<ProtectedRoute><PlayerHubStubPage /></ProtectedRoute>} />
      <Route path="/youth-potential" element={<ProtectedRoute><PlayerHubStubPage /></ProtectedRoute>} />
      <Route path="/mental-health" element={<ProtectedRoute><PlayerHubStubPage /></ProtectedRoute>} />
      <Route path="/market-u" element={<ProtectedRoute><PlayerHubStubPage /></ProtectedRoute>} />
      <Route path="/nsb-online" element={<ProtectedRoute><PlayerHubStubPage /></ProtectedRoute>} />
      {/* SSO launch route — bridges NSB sessions into partner platforms */}
      <Route path="/sso/launch/:partner" element={<ProtectedRoute><SSOLaunchPage /></ProtectedRoute>} />

      {/* Protected Dashboard Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardRedirect />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/athlete"
        element={
          <ProtectedRoute allowedRoles={["athlete"]}>
            <AthleteDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/coach"
        element={
          <ProtectedRoute allowedRoles={["coach", "admin"]}>
            <CoachDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/scout"
        element={
          <ProtectedRoute allowedRoles={["scout", "admin"]}>
            <ScoutDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/admin"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/messages"
        element={
          <ProtectedRoute>
            <MessagingPage />
          </ProtectedRoute>
        }
      />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// Dashboard redirect based on user role
function DashboardRedirect() {
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const getUser = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(`${API_URL}/api/auth/me`, {
          credentials: "include",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });

        if (response.ok) {
          const userData = await response.json();
          setUser(userData);

          const roleRoutes = {
            athlete: "/dashboard/athlete",
            coach: "/dashboard/coach",
            scout: "/dashboard/scout",
            agent: "/dashboard/agent",
            admin: "/dashboard/admin",
          };

          navigate(roleRoutes[userData.role] || "/dashboard/athlete", { replace: true });
        }
      } catch (error) {
        navigate("/login", { replace: true });
      }
    };

    getUser();
  }, [navigate]);

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
      <div className="text-white text-lg font-display">Redirecting...</div>
    </div>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
      <Toaster position="top-right" theme="dark" />
      {/* Noise overlay for texture */}
      <div className="noise-overlay" />
    </div>
  );
}

export default App;
